import React, { useEffect, useState, useCallback } from 'react'
import { EyeIcon, ArrowPathIcon, FunnelIcon, MagnifyingGlassIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import DataTable from '../ui/DataTable'
import Pagination from '../ui/Pagination'
import Select from 'react-select'
import FalabellaService from '../../api/falabellaService'
import { FalabellaProduct, FalabellaFilters } from '../../types/falabella'
import { Button } from '../ui'
import { Switch } from '../ui/Input'

interface FalabellaProductsTableProps {
  onProductSelect?: (product: FalabellaProduct) => void
}

interface StockUpdateModalProps {
  isOpen: boolean
  product: FalabellaProduct | null
  onClose: () => void
  onUpdate: (sku: string, quantity: number) => void
}

const FalabellaProductsTable: React.FC<FalabellaProductsTableProps> = ({ onProductSelect }) => {
  const [products, setProducts] = useState<FalabellaProduct[]>([])
  const [filteredProducts, setFilteredProducts] = useState<FalabellaProduct[]>([])
  const [displayedProducts, setDisplayedProducts] = useState<FalabellaProduct[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(30)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentFilters, setCurrentFilters] = useState<FalabellaFilters>({
    filter: 'active',
    limit: 100
  })

  // Estados para filtros
  const [statusFilter, setStatusFilter] = useState<{ label: string; value: string } | null>({
    label: 'Activos',
    value: 'active'
  })
  const [showZeroQuantity, setShowZeroQuantity] = useState(false)
  const [stockUpdateModal, setStockUpdateModal] = useState<{
    isOpen: boolean
    product: FalabellaProduct | null
  }>({
    isOpen: false,
    product: null
  })

  const statusOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'Activos', value: 'active' },
    { label: 'Inactivos', value: 'inactive' },
    { label: 'Eliminados', value: 'deleted' },
    { label: 'Sin imagen', value: 'image-missing' },
    { label: 'Pendientes', value: 'pending' },
    { label: 'Rechazados', value: 'rejected' },
    { label: 'Agotados', value: 'sold-out' }
  ]

  // Función para cargar productos de Falabella
  const fetchProducts = useCallback(async (filters: FalabellaFilters = {}) => {
    try {
      setIsLoading(true)
      const response = await FalabellaService.getProducts(filters)
      
      if (response.success && response.data?.falabellaResponse?.SuccessResponse?.Body?.Products?.Product) {
        const productsData = response.data.falabellaResponse.SuccessResponse.Body.Products.Product
        setProducts(productsData)
        console.log('📦 Productos de Falabella cargados:', productsData.length)
      } else {
        console.error('Error en la respuesta de Falabella:', response)
        toast.error(response.message || 'Error al cargar productos de Falabella')
        setProducts([])
      }
    } catch (error: any) {
      console.error('Error fetching Falabella products:', error)
      toast.error((t) => (
        <div className="flex items-center justify-between w-full">
          <span>Error al cargar productos de Falabella</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-4 text-black hover:text-gray-200 cursor-pointer text-xl font-bold"
          >
            ×
          </button>
        </div>
      ))
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Función para buscar productos en el servidor
  const searchProducts = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      // Si no hay término de búsqueda, cargar productos normales
      fetchProducts(currentFilters)
      return
    }

    try {
      setIsLoading(true)
      const response = await FalabellaService.searchProducts(searchTerm.trim(), 100, 0)
      
      if (response.success && response.data?.falabellaResponse?.SuccessResponse?.Body?.Products?.Product) {
        const productsData = response.data.falabellaResponse.SuccessResponse.Body.Products.Product
        setProducts(productsData)
        console.log('🔍 Productos encontrados:', productsData.length, 'para:', searchTerm)
      } else {
        console.error('Error en la búsqueda de Falabella:', response)
        toast.error(response.message || 'Error al buscar productos de Falabella')
        setProducts([])
      }
    } catch (error: any) {
      console.error('Error searching Falabella products:', error)
      toast.error('Error al buscar productos de Falabella')
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }, [currentFilters, fetchProducts])

  // Función para aplicar filtros locales
  const applyFilters = useCallback(() => {
    let filtered = [...products]

    // Si hay un término de búsqueda, no aplicar filtros locales ya que la búsqueda se hace en el servidor
    if (debouncedSearchTerm.trim()) {
      setFilteredProducts(filtered)
      setCurrentPage(1)
      return
    }

    // Solo aplicar filtros locales cuando no hay búsqueda activa
    // El filtro de stock ahora se maneja a través del filtro de estado (sold-out)
    // No necesitamos filtrar localmente por stock

    setFilteredProducts(filtered)
    setCurrentPage(1) // Resetear a la primera página
  }, [products, debouncedSearchTerm])

  // Función para obtener productos paginados
  const getPaginatedProducts = useCallback(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginated = filteredProducts.slice(startIndex, endIndex)
    setDisplayedProducts(paginated)
    
    const total = Math.ceil(filteredProducts.length / pageSize)
    setTotalPages(total)
  }, [filteredProducts, currentPage, pageSize])

  // Efecto para debounce del término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      // Llamar a la búsqueda del servidor cuando cambie el término
      searchProducts(searchTerm)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, searchProducts])

  // Efecto para aplicar filtros cuando cambien los criterios
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  // Efecto para paginar cuando cambien los productos filtrados
  useEffect(() => {
    getPaginatedProducts()
  }, [getPaginatedProducts])

  // Carga inicial
  useEffect(() => {
    fetchProducts(currentFilters)
  }, [fetchProducts, currentFilters])

  const handleRefresh = () => {
    fetchProducts(currentFilters)
  }

  const handleFilterChange = (newFilters: Partial<FalabellaFilters>) => {
    const updatedFilters = { ...currentFilters, ...newFilters }
    setCurrentFilters(updatedFilters)
    fetchProducts(updatedFilters)
  }

  const handleStatusFilterChange = (selectedOption: { label: string; value: string } | null) => {
    setStatusFilter(selectedOption)
    if (selectedOption) {
      handleFilterChange({ filter: selectedOption.value as any })
      // Si se selecciona un estado diferente, desactivar el toggle de sold-out
      if (selectedOption.value !== 'sold-out') {
        setShowZeroQuantity(false)
      } else {
        setShowZeroQuantity(true)
      }
    }
  }

  const handleQuantityFilterToggle = (isChecked: boolean) => {
    setShowZeroQuantity(isChecked)
    // Cambiar el filtro de estado para incluir/excluir sold-out
    if (isChecked) {
      // Si se activa, cambiar a 'sold-out' para mostrar solo productos agotados
      handleFilterChange({ filter: 'sold-out' })
    } else {
      // Si se desactiva, volver a 'active' para mostrar solo productos activos
      handleFilterChange({ filter: 'active' })
    }
  }

  const handleUpdateStock = (product: FalabellaProduct) => {
    setStockUpdateModal({
      isOpen: true,
      product: product
    })
  }

  const handleStockUpdate = async (sku: string, quantity: number) => {
    try {
      const response = await FalabellaService.updateStock(sku, quantity)
      
      if (response.success) {
        toast.success('Stock actualizado exitosamente')
        // Recargar productos para mostrar el stock actualizado
        fetchProducts(currentFilters)
      } else {
        toast.error(response.message || 'Error al actualizar el stock')
      }
    } catch (error: any) {
      console.error('Error updating stock:', error)
      toast.error('Error al actualizar el stock')
    } finally {
      setStockUpdateModal({ isOpen: false, product: null })
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Activo' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactivo' },
      deleted: { bg: 'bg-red-100', text: 'text-red-800', label: 'Eliminado' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' },
      'sold-out': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Agotado' },
      'image-missing': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Sin imagen' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const getQCStatusBadge = (qcStatus: string) => {
    const qcConfig = {
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprobado' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' }
    }

    const config = qcConfig[qcStatus as keyof typeof qcConfig] || qcConfig.pending

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const columns = [
    {
      header: 'SKU SGL',
      accessor: 'SellerSku' as keyof FalabellaProduct,
      width: '12%',
      key: 'seller_sku_col'
    },
    {
      header: 'SKU FL',
      accessor: 'ShopSku' as keyof FalabellaProduct,
      width: '10%',
      key: 'shop_sku_col'
    },
    {
      header: 'Nombre',
      accessor: 'Name' as keyof FalabellaProduct,
      width: '25%',
      key: 'name_col',
      cell: (row: FalabellaProduct) => (
        <div className="max-w-xs">
          <p className="font-medium text-gray-900 truncate" title={row.Name}>
            {row.Name}
          </p>
          {row.Variation && (
            <p className="text-xs text-gray-500 truncate" title={row.Variation}>
              {row.Variation}
            </p>
          )}
        </div>
      )
    },
    {
      header: 'Estado',
      accessor: 'BusinessUnits' as keyof FalabellaProduct,
      width: '8%',
      cell: (row: FalabellaProduct) => getStatusBadge(row.BusinessUnits.BusinessUnit.Status),
      key: 'status_col'
    },
    {
      header: 'QC Status',
      accessor: 'QCStatus' as keyof FalabellaProduct,
      width: '8%',
      cell: (row: FalabellaProduct) => getQCStatusBadge(row.QCStatus),
      key: 'qc_status_col'
    },
    {
      header: 'Precio',
      accessor: 'BusinessUnits' as keyof FalabellaProduct,
      width: '10%',
      cell: (row: FalabellaProduct) => {
        const price = parseFloat(row.BusinessUnits.BusinessUnit.Price)
        const specialPrice = parseFloat(row.BusinessUnits.BusinessUnit.SpecialPrice)
        const hasSpecialPrice = specialPrice > 0 && specialPrice < price
        
        return (
          <div className="text-right">
            {hasSpecialPrice ? (
              <div>
                <span className="text-sm text-gray-500 line-through">
                  ${price.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                <br />
                <span className="font-medium text-green-600">
                  ${specialPrice.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            ) : (
              <span className="font-medium text-gray-900">
                ${price.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            )}
          </div>
        )
      },
      key: 'price_col'
    },
    {
      header: 'Stock',
      accessor: 'BusinessUnits' as keyof FalabellaProduct,
      width: '8%',
      cell: (row: FalabellaProduct) => {
        const stock = parseInt(row.BusinessUnits.BusinessUnit.Stock)
        return (
          <span className={`px-2 py-1 rounded-full text-sm font-medium ${
            stock > 0 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {stock}
          </span>
        )
      },
      key: 'stock_col'
    },
    {
      header: 'Marca',
      accessor: 'Brand' as keyof FalabellaProduct,
      width: '8%',
      cell: (row: FalabellaProduct) => row.Brand || '-',
      key: 'brand_col'
    },
    {
      header: 'Categoría',
      accessor: 'PrimaryCategory' as keyof FalabellaProduct,
      width: '10%',
      cell: (row: FalabellaProduct) => (
        <span className="text-xs text-gray-600 truncate" title={row.PrimaryCategory}>
          {row.PrimaryCategory}
        </span>
      ),
      key: 'category_col'
    },
    {
      header: 'Acciones',
      accessor: 'SellerSku' as keyof FalabellaProduct,
      width: '8%',
      cell: (row: FalabellaProduct) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => onProductSelect?.(row)} 
            className="text-blue-500 hover:text-blue-700" 
            title="Ver detalles"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button 
            onClick={() => handleUpdateStock(row)} 
            className="text-green-500 hover:text-green-700" 
            title="Actualizar stock"
          >
            <PencilSquareIcon className="h-5 w-5" />
          </button>
        </div>
      ),
      key: 'actions_col'
    }
  ]

  // Efecto para agregar estilos CSS del toggle
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .toggle-checkbox:checked {
        right: 0;
        border-color: #3b82f6;
      }
      .toggle-checkbox:checked + .toggle-label {
        background-color: #3b82f6;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Componente del modal de actualización de stock
  const StockUpdateModal: React.FC<StockUpdateModalProps> = ({ isOpen, product, onClose, onUpdate }) => {
    const [quantity, setQuantity] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)

    useEffect(() => {
      if (product) {
        setQuantity(product.BusinessUnits.BusinessUnit.Stock)
      }
    }, [product])

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!product || !quantity) return

      const quantityNum = parseInt(quantity)
      if (isNaN(quantityNum) || quantityNum < 0) {
        toast.error('La cantidad debe ser un número válido mayor o igual a 0')
        return
      }

      setIsUpdating(true)
      try {
        await onUpdate(product.SellerSku, quantityNum)
      } finally {
        setIsUpdating(false)
      }
    }

    if (!isOpen || !product) return null

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Actualizar Stock</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Cerrar</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong></strong> {product.Name}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>SKU SGL:</strong> {product.SellerSku}
              </p>
           {/*    <p className="text-sm text-gray-600 mb-4">
                <strong>SKU FL:</strong> {product.ShopSku}
              </p> */}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva cantidad de stock
                </label>
                <input
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ingresa la cantidad"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Stock actual: {product.BusinessUnits.BusinessUnit.Stock}
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
                >
                  {isUpdating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Actualizando...
                    </>
                  ) : (
                    'Actualizar Stock'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-full mx-auto">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <h2 className=" ">Productos de Falabella</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="secondary"
                  leftIcon={FunnelIcon}
                >
                  Filtros
                </Button>
                {showFilters && (
                  <div className="absolute right-0 mt-2 w-96 bg-white shadow-lg rounded-lg p-4 z-10">
                    <div className="space-y-4">
                      {/* Filtro por estado */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado del producto
                        </label>
                        <Select
                          options={statusOptions}
                          value={statusFilter}
                          onChange={handleStatusFilterChange}
                          placeholder="Seleccionar estado..."
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>
                      
                      {/* Filtro por cantidad */}
                      <div className="flex items-center justify-between">
                        <Switch
                          checked={showZeroQuantity}
                          onChange={handleQuantityFilterToggle}
                          label="Mostrar productos agotados"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
              >
                <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Actualizar</span>
              </button>
            </div>
          </div>

          {/* Barra de búsqueda */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre, SKU o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && !isLoading && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Limpiar búsqueda"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
            {isLoading && searchTerm && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>

          <div className="w-full overflow-hidden mb-20">
            <div className="overflow-x-auto">
              <DataTable
                data={displayedProducts}
                columns={columns}
                className="w-full"
              />
            </div>
            {displayedProducts.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg mb-2">
                  {searchTerm ? `No se encontraron productos que coincidan con "${searchTerm}"` : 'No hay productos disponibles'}
                </div>
                <div className="text-gray-400 text-sm">
                  {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Verifica los filtros aplicados'}
                </div>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Modal de actualización de stock */}
      <StockUpdateModal
        isOpen={stockUpdateModal.isOpen}
        product={stockUpdateModal.product}
        onClose={() => setStockUpdateModal({ isOpen: false, product: null })}
        onUpdate={handleStockUpdate}
      />
    </div>
  )
}

export default FalabellaProductsTable
